import React, {useMemo} from 'react';
import type {
  SelectOption,
  TaskDefinitionRequest,
} from 'src/api/ProactApiClient';
import {ModuleEnum, TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import type {DueDatePreviewFormProps} from 'src/app/forms/DueDatePreviewForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {useGetSimpleComplianceStandardListQuery} from 'src/redux/queries/complianceStandard';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {normalizeFormValues} from 'src/types/FormV2Mapping';
import type {
  TaskFeatureEditorProps,
  TaskFeatureFormOptions,
} from './taskFeatureTypes';
import {useResetTaskFeatureForm} from './taskFeatureTypes';

type TransformMeta = TaskFeatureFormOptions['transformMeta'];
const EMPTY_SELECT_OPTIONS: SelectOption[] = [];

export type UseTaskFeatureFormMeta = (
  taskState: TaskDefinitionStateEnum,
  transformMeta?: TransformMeta
) => {formMeta: IFormFieldV2[]; isLoading: boolean; error: unknown};

export const useComplianceStandardOptions = () => {
  const {skipCompliance} = useSkipTokensByFeature();
  const {view} = useModulePermissions(ModuleEnum.Compliance);
  const canLoadCompliance = view && !skipCompliance;
  const query = useGetSimpleComplianceStandardListQuery(undefined, {
    skip: !canLoadCompliance,
  });
  return {
    standards: canLoadCompliance
      ? (query.data?.data ?? EMPTY_SELECT_OPTIONS)
      : EMPTY_SELECT_OPTIONS,
    canViewCompliance: view,
    isLoading: canLoadCompliance && (query.isFetching || query.isLoading),
    error: canLoadCompliance ? query.error : undefined,
  };
};

export const useComplianceFormMeta = (
  getFormMeta: (
    taskState: TaskDefinitionStateEnum,
    standards: SelectOption[]
  ) => IFormFieldV2[],
  taskState: TaskDefinitionStateEnum,
  transformMeta?: TransformMeta
) => {
  const {standards, canViewCompliance, isLoading, error} =
    useComplianceStandardOptions();
  const formMeta = useMemo(() => {
    const fields = getFormMeta(taskState, standards).filter(
      field => canViewCompliance || field.name !== 'complianceStandardIds'
    );
    return transformMeta ? transformMeta(fields) : fields;
  }, [canViewCompliance, getFormMeta, standards, taskState, transformMeta]);
  return {formMeta, isLoading, error};
};

export const createTaskFeatureEditor = (
  useFormMeta: UseTaskFeatureFormMeta,
  mapRequest: (values: IFormDataV2) => TaskDefinitionRequest,
  FormComponent: React.ComponentType<DueDatePreviewFormProps>
): React.FC<TaskFeatureEditorProps> => {
  const TaskFeatureFormEditor: React.FC<TaskFeatureEditorProps> = ({
    children,
    onValuesChange,
    taskState = TaskDefinitionStateEnum.Draft,
    initialData,
    resetKey,
    transformMeta,
  }) => {
    const {formMeta, isLoading, error} = useFormMeta(taskState, transformMeta);
    const formApi = useProactForm({
      formMeta,
      initialData,
      enablePreSubmissionValidation: false,
    });

    useResetTaskFeatureForm(formApi, initialData, resetKey);

    return children({
      formApi,
      isLoading,
      getFeatureFields: () =>
        mapRequest(normalizeFormValues(formMeta, formApi.methods.getValues())),
      form: error ? (
        <ErrorAlert message={parseErrorMessage(error)} />
      ) : (
        <FormComponent
          form={formApi}
          isLoading={isLoading}
          onValuesChange={onValuesChange}
        />
      ),
    });
  };
  return TaskFeatureFormEditor;
};
